import { useEffect, useRef, useState } from "react";
import { ErrorState, RenderMultiInputChipsProps } from "../../../../types";
import { useIntl } from "react-intl";
import { dispatchDeletedObjectIfExists } from "../../helpers";
import { SecondaryButton, IconAdd } from "@cdkglobal/radial";
import styled from "styled-components";
import { noError, validateRowDataInput } from "../../validations";
import { MultiInputChipsWithTooltip } from "../../../common/Tooltip";

const MultiInputContainer = styled.div<{ $disabled: boolean }>`
  display: flex;
  gap: 1rem;
  width: 100%;
  align-items: flex-start;

  [data-testid$="-multi-input"] {
    flex: 1;
    min-width: 21rem;
  }

  [class*="ChipGroup"] {
    width: 200%;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }

  [data-testid$="-add-button"] {
    flex: 0 0 auto;
    min-width: auto;
    padding: 1rem;
  }
  ${({ $disabled }) =>
    $disabled &&
    `
      [data-testid$="-remove-button"] {
        display: none;
      }
    `}
`;

export default function RenderMultiInputChips({
  title,
  description,
  propertyKey,
  propertySubKey,
  inputValues,
  isRequired,
  isDisabled,
  error,
  message,
  selectedTabSectionKey,
  rowDataReducerState,
  dispatch,
}: Readonly<RenderMultiInputChipsProps>): JSX.Element {
  const [multiInputValues, setMultiInputValues] = useState<string[]>([]);
  const [updatedErrorAndMessage, setUpdatedErrorAndMessage] =
    useState<ErrorState>(noError());
  const prevValuesRef = useRef<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const intl = useIntl();
  const input = containerRef.current?.querySelector("input");
  const property = rowDataReducerState.schema.properties[propertyKey];

  useEffect(() => {
    const values = inputValues.map((o) => o.value as string);
    setMultiInputValues(values);
    prevValuesRef.current = values;
  }, [inputValues]);

  useEffect(() => {
    if (!input) return;

    const handleNativeInput = () => {
      if (updatedErrorAndMessage.error) {
        setUpdatedErrorAndMessage(noError());
      }
    };

    input.addEventListener("input", handleNativeInput);

    return () => {
      input.removeEventListener("input", handleNativeInput);
    };
  }, [updatedErrorAndMessage.error]);

  const handleChipsChange = (nextValues: string[]) => {
    const prevValues = prevValuesRef.current;

    const added = nextValues.filter((v) => !prevValues.includes(v));
    const removed = prevValues.filter((v) => !nextValues.includes(v));

    added.forEach((value) => {
      dispatch({
        type: "ADD_MULTI_VALUED_FIELD",
        payload: {
          propertyKey,
          newFieldValue: value,
          propertySubKey,
          selectedTabSectionKey,
        },
      });
    });

    removed.forEach((value) => {
      const index = prevValues.indexOf(value);

      dispatch({
        type: "REMOVE_MULTI_VALUED_FIELD",
        payload: {
          propertyKey,
          rowIndex: index,
          selectedTabSectionKey,
        },
      });

      dispatchDeletedObjectIfExists({
        propertyKey,
        index,
        rowData: rowDataReducerState.rowData,
        selectedTabSectionKey,
        dispatch,
      });
    });

    setMultiInputValues(nextValues);
    prevValuesRef.current = nextValues;
  };

  const handleAdd = () => {
    if (!input?.value) {
      setUpdatedErrorAndMessage({
        error: true,
        message: "Please enter a value",
      });

      return;
    }
    setUpdatedErrorAndMessage(noError());
    input.dispatchEvent(
      new KeyboardEvent("keydown", {
        key: "Enter",
        code: "Enter",
        bubbles: true,
      }),
    );
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      if (!input?.value) {
        setUpdatedErrorAndMessage({
          error: true,
          message: "Please enter a value",
        });
        return;
      }
      if (multiInputValues.includes(input.value)) {
        setUpdatedErrorAndMessage({
          error: true,
          message: `${title} exists`,
        });
      }
      const hasError = validateRowDataInput(
        propertyKey,
        property,
        input.value,
        rowDataReducerState,
        dispatch,
      );
      if (hasError.error) {
        setUpdatedErrorAndMessage(hasError);
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    }
  };

  return (
    <MultiInputContainer
      ref={containerRef}
      data-testid={`${propertyKey}-multi-input-container`}
      $disabled={isDisabled}
    >
      <MultiInputChipsWithTooltip
        dataTestId={`${propertyKey}-multi-input`}
        id={`${propertyKey}-multi-input`}
        onChange={handleChipsChange}
        tooltip={description}
        options={multiInputValues}
        inputProps={{
          id: `${propertyKey}-chips`,
          label: title,
          name: `${propertyKey}-chips`,
          placeholder: isDisabled ? title : intl.formatMessage(
            { id: "placeholder.enter", defaultMessage: "Enter {item}" },
            { item: title },
          ),
          onKeyDown: handleKeyPress,
          isRequired: isRequired && !isDisabled,
          enableIsRequiredValidation: false,
          enableCustomValidation: true,
          hasError: error || updatedErrorAndMessage.error,
          errorMessage: message || updatedErrorAndMessage.message,
          isDisabled,
        }}
      />

      <SecondaryButton
        dataTestId={`${propertyKey}-add-button`}
        icon={<IconAdd />}
        iconSize={24}
        onClick={handleAdd}
        size="large"
        text={"Add"}
        isDisabled={isDisabled}
      />
    </MultiInputContainer>
  );
}
